import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { primaryBtn, secondaryBtn } from '../components/ui/classes';
import { errorCode, householdErrorMessage } from '../components/ui/householdErrors';
import { PageHeader } from '../components/ui/PageHeader';
import { declineInvite, getInviteByToken, joinHousehold, type InviteByToken } from '../data/household';
import { signOut, useAuth } from '../lib/auth';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; invite: InviteByToken }
  | { kind: 'error'; code: string; message: string; invite?: InviteByToken };

// Błędy, po których zaproszenia nie da się już przyjąć — zamiast przycisków pokazujemy wyjaśnienie.
const FINAL = new Set(['wrong_email', 'other_members', 'expired', 'used', 'already_used', 'not_found', 'http_404', 'http_410']);

/** Ekran zgody z linku w e-mailu: /join?token=… */
export function JoinPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const auth = useAuth();
  const myEmail = auth.status === 'signed-in' ? auth.session.email : '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [busy, setBusy] = useState<'join' | 'decline' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    getInviteByToken(token).then(
      (invite) => !cancelled && setState({ kind: 'ready', invite }),
      (e: unknown) => !cancelled && setState({ kind: 'error', code: errorCode(e), message: householdErrorMessage(e) }),
    );
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const join = async (invite: InviteByToken) => {
    setBusy('join');
    setActionError(null);
    try {
      await joinHousehold({ token });
      navigate('/', { replace: true, state: { flash: `Jesteś teraz w domu „${invite.householdName}”.` } });
    } catch (e) {
      const code = errorCode(e);
      if (FINAL.has(code)) setState({ kind: 'error', code, message: householdErrorMessage(e), invite });
      else setActionError(householdErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const decline = async (invite: InviteByToken) => {
    if (!confirm('Odrzucić zaproszenie?')) return;
    setBusy('decline');
    setActionError(null);
    try {
      await declineInvite(invite.id);
      navigate('/', { replace: true, state: { flash: 'Zaproszenie odrzucone.' } });
    } catch (e) {
      setActionError(householdErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const view: State = !token
    ? { kind: 'error', code: 'no_token', message: 'W linku brakuje zaproszenia. Otwórz link z e-maila jeszcze raz.' }
    : state.kind === 'ready' && myEmail && state.invite.email.toLowerCase() !== myEmail.toLowerCase()
      ? { kind: 'error', code: 'wrong_email', message: '', invite: state.invite }
      : state;

  return (
    <>
      <PageHeader title="Zaproszenie" back="/" />
      {view.kind === 'loading' ? (
        <main className="flex flex-1 items-center justify-center p-8 text-stone-500">Wczytuję zaproszenie…</main>
      ) : view.kind === 'ready' ? (
        <>
          <main className="flex flex-1 flex-col justify-center gap-4 p-6 text-center">
            <img src="/logo.png" alt="" className="mx-auto size-20" />
            <h2 className="text-2xl font-bold text-balance">
              {view.invite.invitedByName || 'Ktoś'} zaprasza Cię do domu „{view.invite.householdName}”
            </h2>
            <p className="text-stone-600 dark:text-stone-400">
              Będziecie mieć wspólne listy zakupów i sklepy, a w trakcie zakupów zobaczycie, co już jest w koszyku.
            </p>
            <p className="text-sm text-stone-500">Twoje dotychczasowe listy i sklepy trafią do wspólnego domu.</p>
          </main>
          <div className="safe-bottom sticky bottom-0 flex flex-col gap-2 px-4 pt-3">
            {actionError && <p className="text-center text-sm text-red-600 dark:text-red-400">{actionError}</p>}
            <button type="button" disabled={busy !== null} onClick={() => void join(view.invite)} className={`${primaryBtn} min-h-14 text-lg`}>
              {busy === 'join' ? 'Dołączam…' : 'Dołącz'}
            </button>
            <button type="button" disabled={busy !== null} onClick={() => void decline(view.invite)} className={secondaryBtn}>
              Odrzuć
            </button>
          </div>
        </>
      ) : (
        <JoinError state={view} myEmail={myEmail} onRetry={() => setAttempt((n) => n + 1)} />
      )}
    </>
  );
}

function JoinError({ state, myEmail, onRetry }: { state: Extract<State, { kind: 'error' }>; myEmail: string; onRetry: () => void }) {
  const { code, invite } = state;
  let title = 'Nie udało się wczytać zaproszenia';
  let body = state.message;
  let action = (
    <button type="button" onClick={onRetry} className={primaryBtn}>
      Spróbuj ponownie
    </button>
  );

  if (code === 'wrong_email') {
    title = 'To zaproszenie jest dla kogoś innego';
    body = `Zaproszenie jest dla ${invite?.email ?? 'innego adresu'}. Zalogowano Cię jako ${myEmail}. Wyloguj się i zaloguj tamtym adresem.`;
    action = (
      <button type="button" onClick={() => void signOut()} className={primaryBtn}>
        Wyloguj
      </button>
    );
  } else if (code === 'other_members') {
    title = 'Masz już wspólny dom';
    body = `Jesteś już w domu z innymi osobami. Żeby dołączyć${invite ? ` do „${invite.householdName}”` : ''}, najpierw opuść obecny dom.`;
    action = (
      <Link to="/home" className={primaryBtn}>
        Przejdź do zakładki Dom
      </Link>
    );
  } else if (FINAL.has(code) || code === 'no_token') {
    title = 'Zaproszenie jest nieaktualne';
    action = (
      <Link to="/" replace className={primaryBtn}>
        Przejdź do list
      </Link>
    );
  }

  return (
    <>
      <main className="flex flex-1 flex-col justify-center gap-3 p-6 text-center">
        <h2 className="text-xl font-semibold text-balance">{title}</h2>
        <p className="text-stone-600 dark:text-stone-400">{body}</p>
      </main>
      <div className="safe-bottom sticky bottom-0 flex flex-col gap-2 px-4 pt-3">{action}</div>
    </>
  );
}
